import React, { useState, useEffect } from 'react';


function Question({ questionObj, answers, handleInputChange, handleKeyUp, tankCount, initialRecommendation }) 
{
    //console.log(`Rendering Question - ID: ${questionObj.id}, Question: "${questionObj.question}"`);
    
    // State for "Apply to all" checkbox
    const [applyToAll, setApplyToAll] = useState(false);
    const [applyToAllValue, setApplyToAllValue] = useState('');
    
    // Reset "Apply to all" checkbox when question changes
    useEffect(() => {
        setApplyToAll(false);
        setApplyToAllValue('');
    }, [questionObj?.question]);
    
    // Add function to check if all tanks are AST
    const areAllTanksAST = () => {
        if (tankCount <= 0) return false;
        return Array.from({ length: tankCount }).every((_, index) => 
            answers[`AST OR UST? - Tank ${index + 1}`] === 'AST'
        );
    };

    // Add function to check if product distinguishing is required for interstice
    const isProductDistinguishingRequired = () => {
        return answers['Is Product Distinguishing required for Interstice?'] === 'Yes';
    };

    // Add function to check if product distinguishing is required for sump/dispenser
    const isProductDistinguishingRequiredSump = () => {
        return answers['Is Product Distinguishing required for Sump/Dispenser Sensors?'] === 'Yes';
    };

    // Check if VLD or CITLD is selected
    const isVLDOrCITLDSelected = () => {
        return answers['Would you like VLD or CITLD CODE-P?'] === 'VLD' || 
               answers['Would you like VLD or CITLD CODE-P?'] === 'CITLD Code-P';
    };

    // Skip rendering completely if no question object or if conditions are met
    if (!questionObj || 
        !questionObj.question || 
        (areAllTanksAST() && 
            (questionObj.question === 'Would you like VLD or CITLD CODE-P?' ||
             questionObj.question === `Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?`)) ||
        (isVLDOrCITLDSelected() && 
            questionObj.question === `Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?`) ||
        (isProductDistinguishingRequired() && 
            questionObj.question === '2-conductor field wiring for interstice sensors?') ||
        (isProductDistinguishingRequiredSump() && 
            questionObj.question === '2-conductor field wiring for sump/dispenser sensors?')) {
        return <></>;  // Return empty fragment instead of null
    }

    // function to get options, handling both static and dynamic cases
    const getQuestionOptions = () => {
        if (typeof questionObj.options === 'function') {
            return questionObj.options(answers);
        } else if (typeof questionObj.getOptions === 'function') {
            // For tank/probe count question, pass answers to check the first initial question
            if (questionObj.question === 'How many Tanks/Probes, for level monitoring?') {
                return questionObj.getOptions(answers);
            }
            // For other questions (like serial ports, sensors), pass initialRecommendation
            return questionObj.getOptions(initialRecommendation);
        }
        return questionObj.options || [];
    };

    // Use this function when rendering options
    const options = getQuestionOptions();

    // Handler for "Apply to all" functionality
    const handleApplyToAllChange = (value, questionText, count, prefix = 'Tank') => {
        // Apply the same value to all instances
        for (let i = 0; i < count; i++) {
            const key = `${questionText} - ${prefix} ${i + 1}`;
            const event = { target: { value: value } };
            handleInputChange(event, key);
        }
    };

    // Main function to render dynamic tank fields based on the question type
    const renderDynamicTankFields = (questionText) => {
        const tankCount = parseInt(answers['How many Tanks/Probes, for level monitoring?']) || 1;
        //console.log(`Rendering dynamic fields for ${questionText} with tankCount: ${tankCount}`);
    
        // Handle Remote Overfill Alarm question
        if (questionText === 'Remote Overfill Alarm?')
        {
            const fillLocations = parseInt(answers['How many Fill locations for remote overflow alarms?']) || 1;
            
            if (fillLocations > 1) {
                return (
                    <>
                        <div className="apply-to-all-container">
                            <label className="apply-to-all-label">
                                <input
                                    type="checkbox"
                                    checked={applyToAll}
                                    onChange={(e) => {
                                        setApplyToAll(e.target.checked);
                                        if (!e.target.checked) {
                                            setApplyToAllValue('');
                                        }
                                    }}
                                />
                                <span>Apply to all locations</span>
                            </label>
                        </div>
                        {applyToAll ? (
                            <div className="dynamic-tank-field">
                                <label className="dynamic-tank-label">All Fill Locations</label>
                                <select
                                    onChange={(e) => {
                                        setApplyToAllValue(e.target.value);
                                        handleApplyToAllChange(e.target.value, questionText, fillLocations, 'Location');
                                    }}
                                    value={applyToAllValue}
                                >
                                    <option value="" disabled>Select an option</option>
                                    {options.map((option, i) => (
                                        <option key={i} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            Array.from({ length: fillLocations }).map((_, index) => (
                                <div key={`fill-location-${index}`} className="dynamic-tank-field">
                                    <label className="dynamic-tank-label">
                                        {`Fill Location ${index + 1}`}
                                    </label>
                                    <select
                                        onChange={(e) => handleInputChange(e, `Remote Overfill Alarm? - Location ${index + 1}`)}
                                        value={answers[`Remote Overfill Alarm? - Location ${index + 1}`] || ''}
                                    >
                                        <option value="" disabled>Select an option</option>
                                        {options.map((option, i) => (
                                            <option key={i} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            ))
                        )}
                    </>
                );
            } else {
                return (
                    <div className="dynamic-tank-field">
                        <label className="dynamic-tank-label">Fill Location 1</label>
                        <select
                            onChange={(e) => handleInputChange(e, `Remote Overfill Alarm? - Location 1`)}
                            value={answers[`Remote Overfill Alarm? - Location 1`] || ''}
                        >
                            <option value="" disabled>Select an option</option>
                            {options.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                );
            }
        }

        if (questionText === 'Tank Material Type?' || 
            questionText === 'Opening for Probe?' || 
            questionText === 'What is the Product / Fuel Type in each Tank?' ||
            questionText === 'Inside diameter of tanks?' ||
            questionText === 'Probe Length' ||
            questionText === '2-conductor field wiring for interstice sensors?') {
            
            if (tankCount > 1) {
                return (
                    <>
                        <div className="apply-to-all-container">
                            <label className="apply-to-all-label">
                                <input
                                    type="checkbox"
                                    checked={applyToAll}
                                    onChange={(e) => {
                                        setApplyToAll(e.target.checked);
                                        if (!e.target.checked) {
                                            setApplyToAllValue('');
                                        }
                                    }}
                                />
                                <span>Apply to all tanks</span>
                            </label>
                        </div>
                        {applyToAll ? (
                            <div className="dynamic-tank-field">
                                <label className="dynamic-tank-label">All Tanks</label>
                                <select
                                    onChange={(e) => {
                                        setApplyToAllValue(e.target.value);
                                        handleApplyToAllChange(e.target.value, questionText, tankCount);
                                    }}
                                    value={applyToAllValue}
                                >
                                    <option value="" disabled>Select an option</option>
                                    {options.map((option, i) => (
                                        <option key={i} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            Array.from({ length: tankCount }).map((_, index) => (
                                <div key={`${questionText}-${index}`} className="dynamic-tank-field">
                                    <label className="dynamic-tank-label">
                                        {`Tank ${index + 1}`}
                                    </label>
                                    <select
                                        onChange={(e) => handleInputChange(e, `${questionText} - Tank ${index + 1}`)}
                                        value={answers[`${questionText} - Tank ${index + 1}`] || ''}
                                    >
                                        <option value="" disabled>Select an option</option>
                                        {options.map((option, i) => (
                                            <option key={i} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            ))
                        )}
                    </>
                );
            } else {
                return (
                    <div className="dynamic-tank-field">
                        <label className="dynamic-tank-label">Tank 1</label>
                        <select
                            onChange={(e) => handleInputChange(e, `${questionText} - Tank 1`)}
                            value={answers[`${questionText} - Tank 1`] || ''}
                        >
                            <option value="" disabled>Select an option</option>
                            {options.map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                );
            }
        }
    
        if (questionText === 'How many Sump / Dispenser Sensors?') 
        {
            return renderSumpDispenserFields();
        }

        if (questionText === 'How many Interstice Sensors?') {
            return renderIntersticeSensorFields();
        }

        // Default rendering for dynamic tank fields
        return renderDefaultTankFields(questionText);
    };

    // Function to render Sump/Dispenser Sensor fields
    const renderSumpDispenserFields = () => (
        <>
            {Array.from({ length: tankCount }).map((_, index) => (
                <div key={`Sump/Dispenser-${index}`} className="dynamic-tank-field">
                    <label className="dynamic-tank-label">
                        {`Tank ${index + 1}`}
                    </label>
                    <select
                        onChange={(e) => handleInputChange(e, `How many Sump / Dispenser Sensors? - Tank ${index + 1}`)}
                        value={answers[`How many Sump / Dispenser Sensors? - Tank ${index + 1}`] || ''}
                    >
                        <option value="" disabled>Select an option</option>
                        {options.map((option, i) => (
                            typeof option === 'object' ? (
                                <option key={i} value={option.value} disabled={option.disabled}>
                                    {option.value}
                                </option>
                            ) : (
                                <option key={i} value={option}>{option}</option>
                            )
                        ))}
                    </select>
                </div>
            ))}

            {/* Follow-up question for Product Distinguishing */}
            {answers['How many Sump / Dispenser Sensors?'] > 0 && (
                <div className="follow-up-field">
                    <label>Is Product Distinguishing required?</label>
                    <select
                        onChange={(e) => handleInputChange(e, 'Is Product Distinguishing required?')}
                        value={answers['Is Product Distinguishing required?'] || ''}
                    >
                        <option value="" disabled>Select an option</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                </div>
            )}
        </>
    );

    // Function to render Interstice Sensor fields
    const renderIntersticeSensorFields = () => (
        <>
            {Array.from({ length: tankCount }).map((_, index) => (
                <div key={`Interstice-${index}`} className="dynamic-tank-field">
                    <label className="dynamic-tank-label">
                        {`Tank ${index + 1}`}
                    </label>
                    <select
                        onChange={(e) => handleInputChange(e, `How many Interstice Sensors? - Tank ${index + 1}`)}
                        value={answers[`How many Interstice Sensors? - Tank ${index + 1}`] || ''}
                    >
                        <option value="" disabled>Select an option</option>
                        {options.map((option, i) => (
                            typeof option === 'object' ? (
                                <option key={i} value={option.value} disabled={option.disabled}>
                                    {option.value}
                                </option>
                            ) : (
                                <option key={i} value={option}>{option}</option>
                            )
                        ))}
                    </select>
                </div>
            ))}
            
            {/* Follow-up questions for Tank Material and Diameter */}
            {answers['How many Interstice Sensors?'] > 0 && (
                <>
                    {Array.from({ length: tankCount }).map((_, index) => (
                        <div key={`tank-material-${index}`} className="follow-up-field">
                            <label>{`Tank ${index + 1} Material Type`}</label>
                            <select
                                onChange={(e) => handleInputChange(e, `Tank Material Type? - Tank ${index + 1}`)}
                                value={answers[`Tank Material Type? - Tank ${index + 1}`] || ''}
                            >
                                <option value="" disabled>Select an option</option>
                                <option value="Steel">Steel</option>
                                <option value="Fiberglass">Fiberglass</option>
                            </select>
                        </div>
                    ))}
                    {Array.from({ length: tankCount }).map((_, index) => (
                        <div key={`tank-diameter-${index}`} className="follow-up-field">
                            <label>{`Tank ${index + 1} Diameter`}</label>
                            <select
                                onChange={(e) => handleInputChange(e, `Inside diameter of tanks? - Tank ${index + 1}`)}
                                value={answers[`Inside diameter of tanks? - Tank ${index + 1}`] || ''}
                            >
                                <option value="" disabled>Select an option</option>
                                <option value="4 foot">4 foot</option>
                                <option value="6 foot">6 foot</option>
                                <option value="8 foot">8 foot</option>
                                <option value="10 foot">10 foot</option>
                            </select>
                        </div>
                    ))} 
                </>
            )} 
        </>
    );

    // Function to render default tank fields
    const renderDefaultTankFields = (questionText) => {
        return Array.from({ length: tankCount }).map((_, index) => (
            <div key={`${questionText}-${index}`} className="dynamic-tank-field">
                <label className="dynamic-tank-label">
                    {`Tank ${index + 1}`}
                </label>
                <select
                    onChange={(e) => handleInputChange(e, `${questionText} - Tank ${index + 1}`)}
                    value={answers[`${questionText} - Tank ${index + 1}`] || ''}
                >
                    <option value="" disabled>Select an option</option>
                    {options.map((option, i) => (
                        typeof option === 'object' ? (
                            <option key={i} value={option.value} disabled={option.disabled}>
                                {option.value}
                            </option>
                        ) : (
                            <option key={i} value={option}>{option}</option>
                        )
                    ))}
                </select>
            </div>
        ));
    };


    // For AST/UST question, render tank-specific selections
    if (questionObj.question === 'AST OR UST?') {
        if (tankCount > 1) {
            return (
                <div className="omntec-question-container">
                    <h2>{questionObj.question}</h2>
                    <div className="apply-to-all-container">
                        <label className="apply-to-all-label">
                            <input
                                type="checkbox"
                                checked={applyToAll}
                                onChange={(e) => {
                                    setApplyToAll(e.target.checked);
                                    if (!e.target.checked) {
                                        setApplyToAllValue('');
                                    }
                                }}
                            />
                            <span>Apply to all tanks</span>
                        </label>
                    </div>
                    {applyToAll ? (
                        <div className="dynamic-tank-field">
                            <label className="dynamic-tank-label">All Tanks</label>
                            <select
                                onChange={(e) => {
                                    setApplyToAllValue(e.target.value);
                                    handleApplyToAllChange(e.target.value, 'AST OR UST?', tankCount);
                                }}
                                value={applyToAllValue}
                            >
                                <option value="" disabled>Select an option</option>
                                <option value="AST">AST</option>
                                <option value="UST">UST</option>
                            </select>
                        </div>
                    ) : (
                        Array.from({ length: tankCount }).map((_, index) => (
                            <div key={`AST-UST-${index}`} className="dynamic-tank-field">
                                <label className="dynamic-tank-label">
                                    {`Tank ${index + 1}`}
                                </label>
                                <select
                                    onChange={(e) => handleInputChange(e, `AST OR UST? - Tank ${index + 1}`)}
                                    value={answers[`AST OR UST? - Tank ${index + 1}`] || ''}
                                >
                                    <option value="" disabled>Select an option</option>
                                    <option value="AST">AST</option>
                                    <option value="UST">UST</option>
                                </select>
                            </div>
                        ))
                    )}
                </div>
            );
        } else {
            return (
                <div className="omntec-question-container">
                    <h2>{questionObj.question}</h2>
                    <div className="dynamic-tank-field">
                        <label className="dynamic-tank-label">Tank 1</label>
                        <select
                            onChange={(e) => handleInputChange(e, `AST OR UST? - Tank 1`)}
                            value={answers[`AST OR UST? - Tank 1`] || ''}
                        >
                            <option value="" disabled>Select an option</option>
                            <option value="AST">AST</option>
                            <option value="UST">UST</option>
                        </select>
                    </div>
                </div>
            );
        }
    }

    // Render question with dynamic fields if needed
    return (
        <div className="omntec-question-container">
            <h2>{questionObj?.question}</h2>
            {questionObj?.options || questionObj?.getOptions ? (
                questionObj.question === 'How many Tanks/Probes, for level monitoring?' ? (
                    // Special handling for tank count question
                    <select 
                        onChange={handleInputChange} 
                        value={answers[questionObj?.question] || ''}
                    >
                        <option value="" disabled>Select an option</option>
                        {options.map((option, index) => (
                            <option key={index} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    questionObj.question === 'Tank Material' || 
                    questionObj.question === 'AST OR UST?' || 
                    questionObj.question === 'What is the Product / Fuel Type in each Tank?' || 
                    questionObj.question === 'Inside diameter of tanks?' ||
                    questionObj.question === 'Probe Length' ||
                    questionObj.question === 'Tank Material Type?' ||
                    questionObj.question === 'Opening for Probe?' ||
                    questionObj.question === 'Remote Overfill Alarm?' ||
                    questionObj.question === '2-conductor field wiring for interstice sensors?' ? (
                        renderDynamicTankFields(questionObj.question)
                    ) : (
                        <select 
                            onChange={handleInputChange} 
                            value={answers[questionObj?.question] || ''}
                        >
                            <option value="" disabled>Select an option</option>
                            {options.map((option, index) => (
                                typeof option === 'object' ? (
                                    <option key={index} value={option.value} disabled={option.disabled}>
                                        {option.value}
                                    </option>
                                ) : (
                                    <option key={index} value={option}>
                                        {option}
                                    </option>
                                )
                            ))}
                        </select>
                    )
                )
            ) : (
                <input 
                    type="text" 
                    onKeyUp={handleKeyUp} 
                    onChange={handleInputChange}
                    value={answers[questionObj?.question] || ''}
                />
            )}
            <div className="question-description">
                {questionObj?.description || 'Description goes here'}
            </div>
            {questionObj?.note && (
                <div className="question-note" style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    padding: '10px 15px',
                    marginTop: '10px',
                    color: '#856404',
                    fontSize: '0.9em'
                }}>
                    <strong>Note:</strong> {questionObj.note}
                </div>
            )}
        </div>
    );
}

export default Question;
